import { Injectable } from '@angular/core';
import FileSaver from 'file-saver';
import { DatePipe } from '@angular/common';
import { SalesOrder } from '../models/sales-order';
import { OtherCost } from 'src/app/shared/models/other-cost';
import { SalesOrderFlat } from 'src/app/shared/models/sales-order-flat';
import { MaterialRequirement } from 'src/app/shared/models/material-requirement';
import { AssetDepreciation, RunDetail } from 'src/app/fixed-assets/models/fixed-asset';
import { TranslateService } from '@ngx-translate/core';
import * as ExcelJS from 'exceljs';
import { BillOfMaterial } from 'src/app/manufacturing/models/manufacturing-bill-of-material.model';
import { InvoiceableDocumentError } from 'src/app/invoicing/models';

@Injectable({
  providedIn: 'root'
})
export class ExcelService {

  private EXCEL_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
  private EXCEL_EXTENSION = '.xlsx';
  private salesOrdersHeadersOrder = ['Id', 'Document Code', 'Document Date', 'Paper Number', 'Customer Id', 'Customer Name',
    'Salesperson Id', 'Salesperson Name', 'Warehouse Id', 'Warehouse Name', 'Gross Total', 'Sub Total', 'Net Total', 'Order Status'];


  private materialRequirementsHeadersOrder = ['Material Item Code', 'Material Item Description', 'Quantity', 'Available Balance', 'Expected Receive',
    'Quantity To Purchase'];
  private otherCostsHeadersOrder = ['Cost Type Description', 'Gl Account Description', 'Quantity', 'Cost Per Unit', 'Total'];
  private productionPlanHeadersOrder = ['Product', 'Sales document', 'Customer', 'Sales person', 'Quantity', 'SO status'];
  private depreciationRunHeadersOrder = ['Asset Type', 'Asset Account', 'Accumulated Depreciation Account', 'Depreciation Expense Account', 'Carrying Value'];
  private depreciationSummaryHeadersOrder = ['Asset Name', 'Asset Code', 'Asset Type', 'Historical Cost', 'Opening Accumulated', 'Opening Carrying Value',
    'Purchases', 'Disposals', 'Depreciations', 'Closing Value', 'Current Location', 'Previous Location'];
  private reconciliationDetailsHeadersOrder = [
    this.translateService.instant('productionOrder.reconcile.productionOrder'),
    this.translateService.instant('productionOrder.reconcile.product'),
    this.translateService.instant('productionOrder.reconcile.costPerUnit'),
    this.translateService.instant('productionOrder.reconcile.unitCostChange'),
    this.translateService.instant('productionOrder.reconcile.costPerUnitAfterReconciliation'),
    this.translateService.instant('productionOrder.reconcile.producedQty'),
    this.translateService.instant('productionOrder.reconcile.totalCostImpact'),
  ];
  private reconciliationResultsHeadersOrder = [
    this.translateService.instant('productionOrder.reconcile.productionOrder'),
    this.translateService.instant('productionOrder.reconcile.failureReason')
  ];
  private bulkStartResultsHeadersOrder = [
    this.translateService.instant('productionOrder.start.productionOrder'),
    this.translateService.instant('productionOrder.start.failureReason')
  ];
  private bulkCompleteResultsHeadersOrder = [
    this.translateService.instant('productionOrder.complete.productionOrder'),
    this.translateService.instant('productionOrder.complete.failureReason')
  ];
  private invoiceableDocumentErrorsHeadersOrder = ['Type', 'Source', 'Issue'];

  constructor(private translateService: TranslateService) { }

  public exportSalesOrders(salesOrders: any[], excelFileName: string) {
    const newJson = salesOrders.map((res: SalesOrder) => {
      return {
        'Id': res.id,
        'Document Code': res.document_code,
        'Document Date': new DatePipe('en-US').transform(res.document_date, 'medium'),
        'Paper Number': res.paper_number,
        'Customer Id': res.customer_id,
        'Customer Name': res.customer?.name,
        'Salesperson Id': res.salesPerson_id,
        'Salesperson Name': res.salesperson?.name,
        'Warehouse Id': res.warehouse_id,
        'Warehouse Name': res.warehouse?.description,
        'Gross Total': res.gross_total,
        'Sub Total': res.sub_total,
        'Net Total': res.net_total,
        'Order Status': res.order_status
      };
    });

    this.exportAsExcelFile(newJson, this.salesOrdersHeadersOrder, excelFileName);
  }

  public exportMaterialRequirements(materialRequirements: any[], excelFileName: string) {
    const newJson = materialRequirements.map((res: MaterialRequirement) => {
      return {
        'Material Item Code': res.material_item_code,
        'Material Item Description': res.material_item_description,
        'Quantity': res.quantity,
        'Available Balance': res.available_balance,
        'Expected Receive': res.expected_receive,
        'Quantity To Purchase': res.quantity_to_purchase
      };
    });

    this.exportAsExcelFile(newJson, this.materialRequirementsHeadersOrder, excelFileName);
  }

  public exportOtherCosts(otherCosts: any[], excelFileName: string) {
    const newJson = otherCosts.map((res: OtherCost) => {
      return {
        'Cost Type Description': res.cost_type.description,
        'Gl Account Description': res.glaccount_description,
        'Quantity': res.quantity,
        'Cost Per Unit': res.cost_per_unit,
        'Total': res.total
      };
    });

    this.exportAsExcelFile(newJson, this.otherCostsHeadersOrder, excelFileName);
  }

  public exportProductionPlanItems(productionPlanItems: any[], excelFileName: string) {
    const newJson = productionPlanItems.map((res: SalesOrderFlat) => {
      return {
        'Product': res.stock_item_description,
        'Sales document': res.document_code,
        'Customer': res.customer_name,
        'Sales person': res.salesperson_name,
        'Quantity': res.quantity,
        'SO status': res.order_status
      };
    });

    this.exportAsExcelFile(newJson, this.productionPlanHeadersOrder, excelFileName);
  }

  public exportDepreciationRun(runDetail: RunDetail[], excelFileName: string) {
    const newJson = runDetail.map((res: RunDetail) => {
      return {
        'Asset Type': res.asset_type,
        'Asset Account': res.asset_account_name,
        'Accumulated Depreciation Account': res.accumulated_depreciation_name,
        'Depreciation Expense Account': res.depreciation_expense_name,
        'Carrying Value': res.carrying_value
      };
    });

    this.exportAsExcelFile(newJson, this.depreciationRunHeadersOrder, excelFileName);
  }

  public exportDepreciationSummary(assetDepreciation: AssetDepreciation[], monthlyDetails: any[], excelFileName: string) {
    const workbook = new ExcelJS.Workbook();

    const dataWorksheet = workbook.addWorksheet('data');
    dataWorksheet.addRow(this.depreciationSummaryHeadersOrder);

    assetDepreciation.forEach(res => {
      const row = this.depreciationSummaryHeadersOrder.map(h => {
        switch (h) {
          case 'Asset Name': return res.asset_name;
          case 'Asset Code': return res.asset_code;
          case 'Asset Type': return res.asset_type;
          case 'Historical Cost': return res.historical_cost;
          case 'Opening Accumulated': return res.opening_accumulated_depreciation;
          case 'Opening Carrying Value': return res.opening_carrying_value;
          case 'Purchases': return res.purchases;
          case 'Disposals': return res.disposal;
          case 'Depreciations': return res.depreciation_value;
          case 'Closing Value': return res.closing_value;
          case 'Current Location': return res.current_location;
          case 'Previous Location': return res.previous_location;
          default: return '';
        }
      });
      dataWorksheet.addRow(row);
    });

    const monthlySheet = workbook.addWorksheet('Monthly details');

    if (monthlyDetails.length > 0) {
      monthlySheet.addRow(Object.keys(monthlyDetails[0]));
      monthlyDetails.forEach(item => {
        monthlySheet.addRow(Object.values(item));
      });
    }
    const excelBuffer: Promise<ArrayBuffer> = workbook.xlsx.writeBuffer();
    excelBuffer.then(buffer => {
      this.saveAsExcelFile(buffer, excelFileName);
    });
  }

  public exportReconciliationDetails(reconciliationDetails: any[], excelFileName: string) {
    const newJson = reconciliationDetails.map(res => {
      let result: { [key: string]: any } = {};
      result[this.translateService.instant('productionOrder.reconcile.productionOrder')] = res.POCode;
      result[this.translateService.instant('productionOrder.reconcile.product')] = res.ProductName;
      result[this.translateService.instant('productionOrder.reconcile.costPerUnit')] = res.OldValue;
      result[this.translateService.instant('productionOrder.reconcile.unitCostChange')] = res.ChangeValue;
      result[this.translateService.instant('productionOrder.reconcile.costPerUnitAfterReconciliation')] = res.NewValue;
      result[this.translateService.instant('productionOrder.reconcile.producedQty')] = res.ProducedQty;
      result[this.translateService.instant('productionOrder.reconcile.totalCostImpact')] = res.TotalCostImpact;
      return result;
    });

    this.exportAsExcelFile(newJson,
      this.translateService.currentLang === 'en' ? this.reconciliationDetailsHeadersOrder : this.reconciliationDetailsHeadersOrder.reverse(),
      excelFileName);
  }

  public exportReconciliationResults(reconciliationResults: any[], excelFileName: string) {
    const newJson = reconciliationResults.map(res => {
      let result: { [key: string]: any } = {};
      result[this.translateService.instant('productionOrder.reconcile.productionOrder')] = res.POCode;
      result[this.translateService.instant('productionOrder.reconcile.failureReason')] = res.Error;
      return result;
    });

    this.exportAsExcelFile(newJson,
      this.translateService.currentLang === 'en' ? this.reconciliationResultsHeadersOrder : this.reconciliationResultsHeadersOrder.reverse(),
      excelFileName);
  }

  public exportExportBulkStartResults(bulkStartResults: any[], excelFileName: string) {
    const newJson = bulkStartResults.map(res => {
      let result: { [key: string]: any } = {};
      result[this.translateService.instant('productionOrder.start.productionOrder')] = res.POCode;
      result[this.translateService.instant('productionOrder.start.failureReason')] = res.Error;
      return result;
    });

    this.exportAsExcelFile(newJson,
      this.translateService.currentLang === 'en' ? this.bulkStartResultsHeadersOrder : this.bulkStartResultsHeadersOrder.reverse(),
      excelFileName);
  }

  public exportExportBulkCompleteResults(bulkCompleteResults: any[], excelFileName: string) {
    const newJson = bulkCompleteResults.map(res => {
      let result: { [key: string]: any } = {};
      result[this.translateService.instant('productionOrder.complete.productionOrder')] = res.POCode;
      result[this.translateService.instant('productionOrder.complete.failureReason')] = res.Error;
      return result;
    });

    this.exportAsExcelFile(newJson,
      this.translateService.currentLang === 'en' ? this.bulkCompleteResultsHeadersOrder : this.bulkCompleteResultsHeadersOrder.reverse(),
      excelFileName);
  }

  public exportBillOfMaterials(boms: BillOfMaterial[], excelFileName: string) {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("BOMs");
    const isArabic = this.translateService.currentLang === 'ar';

    const labelCol = isArabic ? 'B' : 'A';
    const valueCol = isArabic ? 'A' : 'B';

    const thinBorder: Partial<ExcelJS.Borders> = {
      top: { style: 'thin' as ExcelJS.BorderStyle },
      left: { style: 'thin' as ExcelJS.BorderStyle },
      bottom: { style: 'thin' as ExcelJS.BorderStyle },
      right: { style: 'thin' as ExcelJS.BorderStyle }
    };

    let rowPointer = 1;

    for (let i = 0; i < boms.length; i++) {
      const bom = boms[i];
      const bomStartRow = rowPointer;

      worksheet.mergeCells(`${labelCol}${rowPointer}:${valueCol}${rowPointer}`);
      worksheet.getCell(`${labelCol}${rowPointer}`).value = bom.code.fullCode;
      worksheet.getCell(`${labelCol}${rowPointer}`).font = { bold: true };
      rowPointer++;

      worksheet.getCell(`${labelCol}${rowPointer}`).value = this.translateService.instant('bom.filterPanel.product');
      worksheet.getCell(`${valueCol}${rowPointer}`).value = bom.product_description;
      worksheet.getCell(`${labelCol}${rowPointer}`).font = { bold: true };
      worksheet.getCell(`${labelCol}${rowPointer}`).border = thinBorder;
      worksheet.getCell(`${valueCol}${rowPointer}`).border = thinBorder;
      rowPointer++;

      worksheet.getCell(`${labelCol}${rowPointer}`).value = this.translateService.instant('bom.bomForm.description');
      worksheet.getCell(`${valueCol}${rowPointer}`).value = bom.description;
      worksheet.getCell(`${labelCol}${rowPointer}`).font = { bold: true };
      worksheet.getCell(`${labelCol}${rowPointer}`).border = thinBorder;
      worksheet.getCell(`${valueCol}${rowPointer}`).border = thinBorder;
      rowPointer++;

      worksheet.getCell(`${labelCol}${rowPointer}`).value = this.translateService.instant('bom.bomForm.yieldQty');
      worksheet.getCell(`${valueCol}${rowPointer}`).value = bom.yield_quantity;
      worksheet.getCell(`${labelCol}${rowPointer}`).font = { bold: true };
      worksheet.getCell(`${labelCol}${rowPointer}`).border = thinBorder;
      worksheet.getCell(`${valueCol}${rowPointer}`).border = thinBorder;
      rowPointer++;

      worksheet.getCell(`${labelCol}${rowPointer}`).value = this.translateService.instant('bom.filterPanel.owner');
      worksheet.getCell(`${valueCol}${rowPointer}`).value = bom.owner;
      worksheet.getCell(`${labelCol}${rowPointer}`).font = { bold: true };
      worksheet.getCell(`${labelCol}${rowPointer}`).border = thinBorder;
      worksheet.getCell(`${valueCol}${rowPointer}`).border = thinBorder;
      rowPointer++;

      worksheet.getCell(`${labelCol}${rowPointer}`).value = this.translateService.instant('bom.filterPanel.editors');
      worksheet.getCell(`${valueCol}${rowPointer}`).value = bom.editors.map(e => e.email).join(", ");
      worksheet.getCell(`${labelCol}${rowPointer}`).font = { bold: true };
      worksheet.getCell(`${labelCol}${rowPointer}`).border = thinBorder;
      worksheet.getCell(`${valueCol}${rowPointer}`).border = thinBorder;
      rowPointer++;

      worksheet.addRow([]);
      rowPointer++;

      worksheet.addRow([this.translateService.instant('bom.bomForm.materialCost')]).font = { bold: true };
      rowPointer++;

      let matHeaders = [
        this.translateService.instant('bom.bomForm.description'),
        this.translateService.instant('bom.bomForm.quantity'),
        this.translateService.instant('bom.bomForm.costPerUnit'),
        this.translateService.instant('bom.bomForm.total')
      ];

      if (isArabic)
        matHeaders = matHeaders.reverse();

      const matHeaderRow = worksheet.addRow(matHeaders);
      matHeaderRow.eachCell(cell => {
        cell.font = { bold: true };
        cell.border = thinBorder;
        cell.alignment = { horizontal: "center" };
      });
      rowPointer++;

      if (bom.material_cost_items?.length > 0) {
        bom.material_cost_items.forEach((m: any) => {
          let data = [m.description, m.quantity, m.cost_per_unit, m.total];
          if (isArabic)
            data = data.reverse();
          const row = worksheet.addRow(data);
          row.eachCell(cell => cell.border = thinBorder);
          rowPointer++;
        });
      }
      worksheet.addRow([]);
      rowPointer++;

      worksheet.addRow([this.translateService.instant('bom.bomForm.otherCost')]).font = { bold: true };
      rowPointer++;

      let otherHeaders = [
        this.translateService.instant('bom.bomForm.costType'),
        this.translateService.instant('bom.bomForm.quantity'),
        this.translateService.instant('bom.bomForm.costPerUnit'),
        this.translateService.instant('bom.bomForm.total')
      ];

      if (isArabic)
        otherHeaders = otherHeaders.reverse();

      const otherHeaderRow = worksheet.addRow(otherHeaders);
      otherHeaderRow.eachCell(cell => {
        cell.font = { bold: true };
        cell.border = thinBorder;
        cell.alignment = { horizontal: "center" };
      });
      rowPointer++;

      if (bom.other_cost_items?.length > 0) {
        bom.other_cost_items.forEach((c: any) => {
          let data = [c.cost_type.description, c.quantity, c.cost_per_unit, c.total];
          if (isArabic)
            data = data.reverse();
          const row = worksheet.addRow(data);
          row.eachCell(cell => cell.border = thinBorder);
          rowPointer++;
        });
      }
      worksheet.addRow([]);
      rowPointer++;
      worksheet.addRow([]);
      rowPointer++;

      const bomEndRow = rowPointer - 2;

      for (let r = bomStartRow; r <= bomEndRow; r++) {
        for (let c = 1; c <= 4; c++) {
          worksheet.getRow(r).getCell(c).fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFF3F3F3' }
          }
        };
      }

    }

    const excelBuffer: Promise<ArrayBuffer> = workbook.xlsx.writeBuffer();
    excelBuffer.then(buffer => {
      this.saveAsExcelFile(buffer, excelFileName);
    });
  }

  public exportInvoiceableDocumentErrors(errors: InvoiceableDocumentError[], excelFileName: string) {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('data');

    worksheet.addRow(this.invoiceableDocumentErrorsHeadersOrder);

    errors.forEach(res => {
      worksheet.addRow([res.type, res.source, res.issue]);
    });

    worksheet.columns.forEach((col, index) => {
      let maxLength = this.invoiceableDocumentErrorsHeadersOrder[index]?.length ?? 10;
      errors.forEach(res => {
        const value = [res.type, res.source, res.issue][index];
        const length = value ? value.toString().length : 0;
        if (length > maxLength) maxLength = length;
      });
      col.width = maxLength + 4;
    });

    const excelBuffer: Promise<ArrayBuffer> = workbook.xlsx.writeBuffer();
    excelBuffer.then(buffer => {
      this.saveAsExcelFile(buffer, excelFileName);
    });
  }

  public exportKnownDevicesSecurityLog(rows: any[], header: string[], fileName: string): void {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('WebID log');

    worksheet.addRow(header);
    rows.forEach(row => {
      worksheet.addRow(header.map(h => row[h]));
    });

    worksheet.views = [{ state: 'frozen', ySplit: 1 }];
    worksheet.getRow(1).font = { bold: true };

    worksheet.columns.forEach((col, index) => {
      let maxLength = header[index]?.length ?? 10;
      rows.forEach(row => {
        const value = row[header[index]];
        const length = value ? value.toString().length : 0;
        if (length > maxLength) maxLength = length;
      });
      col.width = Math.min(maxLength + 4, 60);
    });

    const excelBuffer: Promise<ArrayBuffer> = workbook.xlsx.writeBuffer();
    excelBuffer.then(buffer => {
      const data: Blob = new Blob([buffer], { type: this.EXCEL_TYPE });
      FileSaver.saveAs(data, fileName);
    });
  }

  exportAsExcelFile(json: any[], header: string[], excelFileName: string): void {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('data');

    worksheet.addRow(header);

    json.forEach(item => {
      const row = header.map(h => item[h]);
      worksheet.addRow(row);
    });
    const excelBuffer: Promise<ArrayBuffer> = workbook.xlsx.writeBuffer();
    excelBuffer.then(buffer => {
      this.saveAsExcelFile(buffer, excelFileName);
    });
  }

  private saveAsExcelFile(buffer: any, fileName: string): void {
    const data: Blob = new Blob([buffer], {
      type: this.EXCEL_TYPE
    });
    FileSaver.saveAs(data, fileName + '_export_' + new Date().getTime() + this.EXCEL_EXTENSION);
  }
}
